import { Controller } from '@hotwired/stimulus'
import { DirectUpload } from '@rails/activestorage'

export default class extends Controller {
  static targets = ['fileInput', 'hiddenInput', 'spinner', 'errorMessage', 'list', 'template', 'submit']

  static values = {
    fileIdPlaceholder: { type: String, default: 'file_id' }
  }

  declare readonly fileInputTarget: HTMLInputElement
  declare readonly hiddenInputTargets: HTMLInputElement[]
  declare readonly spinnerTargets: HTMLElement[]
  declare readonly errorMessageTargets: HTMLElement[]
  declare readonly listTarget: HTMLElement
  declare readonly templateTarget: HTMLTemplateElement
  declare readonly submitTarget: HTMLInputElement
  declare readonly fileIdPlaceholderValue: string

  // Do not send file in form data, using signed_id in hidden field instead
  submit (): void {
    this.fileInputTarget.disabled = true
  }

  // For those which dont reload the form, eg. settings/users
  submitSuccess (): void {
    this.fileInputTarget.disabled = false
  }

  upload ({ dataTransfer, target }: { dataTransfer: DataTransfer | null, target: EventTarget }): void {
    this.addFiles(dataTransfer ?? target)
  }

  dragOver (e: Event): void {
    e.preventDefault()
  }

  remove ({ params: { index } }: { params: { index: any } }): void {
    const element = document.getElementById(index)

    if (element != null) element.remove()
  }

  private addFiles (input: any, limit: number = input.files.length): void {
    if (limit === 0) return

    const baseIndex = this.hiddenInputTargets.length

    this.toggleDisabled(true)

    let i = 0
    while (i < limit) {
      const file = input.files[i]

      this.add(file, baseIndex + i)
      i++
    }
  }

  private add (file: File, index: number): void {
    const id = `${file.name}-${new Date().getTime()}`
    const fileHTML = this.templateFor(id, file)

    this.listTarget.insertAdjacentHTML('beforeend', fileHTML)
    this.directUpload(file, index)
  }

  private directUpload (file: File, index: number): void {
    const url = this.fileInputTarget.dataset.directUploadUrl

    if (url === null || url === undefined) {
      return
    }

    this.spinnerTargets[index].classList.remove('hidden')

    const directUpload = new DirectUpload(file, url)

    directUpload.create((error, blob) => {
      this.spinnerTargets[index].classList.add('hidden')
      this.toggleDisabled(false)

      if (error !== null) {
        this.errorMessageTargets[index].classList.remove('hidden')
      } else {
        this.hiddenInputTargets[index].value = blob.signed_id
        this.dispatch('uploaded')
      }
    })
  }

  private templateFor (id: string, file: File): string {
    const template = this.templateTarget.innerHTML
      .replace(new RegExp(this.fileIdPlaceholderValue, 'g'), id)
      .replace(/file_name/g, file.name)

    return template
  }

  private toggleDisabled (disabled: boolean): void {
    if (!disabled && this.spinnerTargets.some((spinner) => !spinner.classList.contains('hidden'))) return

    this.fileInputTarget.disabled = disabled
    this.element.querySelector(`label[for=${this.fileInputTarget.id}]`)?.classList.toggle('disabled', disabled)
    this.submitTarget.disabled = disabled
  }
}
